import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { BasePanel } from "components/BasePanel";
import { Button } from "components/button";
import { EmptyList } from "components/empty/EmptyList";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { useProduct } from "contexts/ProductContext";

import { BaseItem, SortableList } from "components/SortableList";

import { useSubjects } from "hooks/useSubjects";
import { Section } from "./Section";
import { SearchThemaCodesModal } from "../SearchThemaCodesModal";
import { createListItems, filterQualifiers, filterSubjects } from "../../utils";

export const FinalThemaList = () => {
  const { t } = useTranslation(["productDetails"]);
  const { product } = useProduct();
  const { handleChangeOrder, handleToggleSubject } = useSubjects();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const finalSubjects = useMemo(
    () => filterSubjects(product?.subjects?.final || []),
    [product],
  );
  const finalQualifiers = useMemo(
    () => filterQualifiers(product?.subjects?.final || []),
    [product],
  );

  const finalSubjectsListItems = useMemo(
    () => createListItems(finalSubjects),
    [finalSubjects],
  );
  const finalQualifiersListItems = useMemo(
    () => createListItems(finalQualifiers),
    [finalQualifiers],
  );

  const handleChangeOrderWrapper =
    (isSubjectList: boolean) => (items: BaseItem[]) => {
      if (product) {
        handleChangeOrder(
          items,
          product.id,
          isSubjectList ? "subject" : "qualifier",
        );
      }
    };

  const handleRemoveFinalSubjectsListItem = useCallback(
    (item: BaseItem | undefined) => {
      if (!item) return;
      const subjectItem = product?.subjects?.final?.find(
        (subject) => subject.subjectCode === item.value,
      );
      if (subjectItem) handleToggleSubject(subjectItem);
    },
    [handleToggleSubject, product?.subjects?.final],
  );

  if (!product) return null;

  return (
    <>
      <BasePanel
        title={t("thema.headers.final")}
        className="w-90 h-full"
        headerClassName="bg-primary-50"
        headerAction={
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="link"
                icon="plus"
                testId="add-thema-code"
                aria-label={t(
                  "thema.searchThemaModal.tooltips.addCodeManually",
                )}
                onClick={() => setIsModalOpen(true)}
                className="px-0"
              />
            </TooltipTrigger>
            <TooltipContent>
              {t("thema.searchThemaModal.tooltips.addCodeManually")}
            </TooltipContent>
          </Tooltip>
        }
      >
        <div className="flex flex-col h-full overflow-y-auto">
          <Section
            title={t("thema.sections.finalSubjects.section.subjects")}
            contentLength={finalSubjectsListItems.length}
          >
            {!finalSubjects.length && (
              <EmptyList
                title={t("thema.sections.finalSubjects.noSubjects.header")}
                text={t("thema.sections.finalSubjects.noSubjects.description")}
                hideOnShortScreen
              />
            )}
            <SortableList
              items={finalSubjectsListItems}
              onChangeOrder={handleChangeOrderWrapper(true)}
              onRemove={handleRemoveFinalSubjectsListItem}
              copyToClipboard
              successToastMessage={t("productDetails:toasts.copyThemaCode")}
            />
          </Section>
          <Section
            title={t("thema.sections.finalSubjects.section.qualifiers")}
            contentLength={finalQualifiersListItems.length}
          >
            {!finalQualifiers.length && (
              <EmptyList
                title={t("thema.sections.finalSubjects.noQualifiers.header")}
                text={t(
                  "thema.sections.finalSubjects.noQualifiers.description",
                )}
                hideOnShortScreen
              />
            )}
            <SortableList
              items={finalQualifiersListItems}
              onChangeOrder={handleChangeOrderWrapper(false)}
              onRemove={handleRemoveFinalSubjectsListItem}
              copyToClipboard
              successToastMessage={t("productDetails:toasts.copyThemaCode")}
            />
          </Section>
        </div>
      </BasePanel>

      <SearchThemaCodesModal open={isModalOpen} onOpenChange={setIsModalOpen} />
    </>
  );
};
